import { Stack } from "@fluentui/react";
import { PanelLeftExpandRegular } from "@fluentui/react-icons";
import React, { useEffect, useRef, useState } from "react";
import { useTenantConfig } from "../../admin/components/TenantThemeProvider";
import { UserMenu } from "../../components/UserMenu/UserMenu";
import { STATIC_HEADER_ICON_PATH } from "../../utils/branding";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useViewportKeyboardInsets } from "../../hooks/useViewportKeyboardInsets";
import styles from "./Layout.module.css";

const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_DEFAULT_WIDTH = 280;
const SIDEBAR_WIDTH_KEY = "cordexa_sidebarWidth";

const readStoredSidebarWidth = (): number => {
  try {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (stored) {
      const width = parseInt(stored, 10);
      if (!Number.isNaN(width) && width >= SIDEBAR_MIN_WIDTH) {
        return width;
      }
    }
  } catch {
    /* ignore */
  }
  return SIDEBAR_DEFAULT_WIDTH;
};

export type LayoutProps = {
  children: React.ReactNode;
  chatHistoryPanel?: React.ReactNode;
  identityName?: string;
  identityEmail?: string;
  onSignOut?: () => void;
  onCollapseSidebar?: () => void;
  isSidebarCollapsed?: boolean;
  onSidebarCollapseChange?: (collapsed: boolean) => void;
};

export function Layout({
  children,
  chatHistoryPanel,
  identityName,
  identityEmail,
  onSignOut,
  isSidebarCollapsed: collapsedProp,
  onSidebarCollapseChange,
}: LayoutProps) {
  const { tenantConfig } = useTenantConfig();
  const isMobile = useIsMobile();
  const insets = useViewportKeyboardInsets(isMobile);
  const [sidebarWidth, setSidebarWidth] = useState(readStoredSidebarWidth);
  const [collapsedLocal, setCollapsedLocal] = useState(isMobile);
  const isSidebarCollapsed = collapsedProp ?? collapsedLocal;
  const setIsSidebarCollapsed = (next: boolean) => {
    setCollapsedLocal(next);
    onSidebarCollapseChange?.(next);
  };
  // Below COMPACT_SHELL_MAX_WIDTH_PX the docked 280px rail would leave ~110px
  // of content. Collapse onto the existing expand/collapse control so the
  // content region owns the viewport; opening the rail uses the existing
  // drawer overlay, not a squeezed column (extends_canonical_path).
  useEffect(() => {
    setIsSidebarCollapsed(isMobile);
  }, [isMobile]);
  const isDraggingRef = useRef(false);
  const headerTitle = "Cortex";
  const persistSidebarWidth = (width: number) => {
    setSidebarWidth(width);
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
    } catch {
      /* ignore */
    }
  };
  const getMaxSidebarWidth = () =>
    typeof window !== "undefined" ? Math.floor(window.innerWidth * 0.5) : 400;
  const handleDragStart = (event: React.MouseEvent) => {
    event.preventDefault();
    isDraggingRef.current = true;
    const startX = event.clientX;
    const startWidth = sidebarWidth;
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) {
        return;
      }
      let next = startWidth + (moveEvent.clientX - startX);
      next = Math.max(SIDEBAR_MIN_WIDTH, Math.min(getMaxSidebarWidth(), next));
      persistSidebarWidth(next);
    };
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const layoutCssVars = {
    "--cordexa-mobile-header-h": isMobile ? "56px" : "64px",
    "--cordexa-header-h": isMobile ? "56px" : "64px",
    "--cordexa-keyboard-inset": `${insets.keyboardInsetBottom}px`,
    "--cordexa-safe-bottom": `${insets.safeAreaBottom}px`,
  } as React.CSSProperties;
  const brandThemeVars = {
    "--accent-color": tenantConfig?.accent_color || "#6a16f0",
    "--secondary-color": tenantConfig?.secondary_color || "#f8fafc",
  } as React.CSSProperties;
  const showRail = Boolean(chatHistoryPanel) && !isSidebarCollapsed;
  const dockedRail = showRail && !isMobile;
  const overlayRail = showRail && isMobile;

  return (
    <div
      className={styles.layout}
      data-testid="console-shell"
      style={{ ...brandThemeVars, ...layoutCssVars }}
    >
      <header className={styles.header} role={"banner"}>
        <div className={styles.headerContainer}>
          <Stack horizontal verticalAlign="center">
            {isSidebarCollapsed && chatHistoryPanel ? (
              <button
                type="button"
                className={styles.expandButton}
                onClick={() => setIsSidebarCollapsed(false)}
                aria-label="Expand sidebar"
                data-testid="console-expand-sidebar"
              >
                <PanelLeftExpandRegular className={styles.expandIcon} />
              </button>
            ) : null}
            <img
              src={STATIC_HEADER_ICON_PATH}
              className={styles.headerIcon}
              aria-hidden="true"
              alt="Cortex"
            />
            <a href="/" className={styles.headerTitleContainer}>
              <h3 className={styles.headerTitle} data-testid="console-page-title">
                {headerTitle}
              </h3>
            </a>
          </Stack>
          <Stack horizontal className={styles.layoutRightButtons} tokens={{ childrenGap: 12 }}>
            {identityName && onSignOut ? (
              <div data-testid="console-identity">
                <UserMenu
                  displayName={identityName}
                  email={identityEmail || ""}
                  onSignOut={onSignOut}
                />
              </div>
            ) : null}
          </Stack>
        </div>
      </header>
      <div className={styles.container}>
        {overlayRail ? (
          <button
            type="button"
            className={styles.mobileScrim}
            aria-label="Close sidebar"
            onClick={() => setIsSidebarCollapsed(true)}
          />
        ) : null}
        {showRail ? (
          <div
            className={
              overlayRail
                ? `${styles.chatHistorySection} ${styles.mobileDrawer} ${styles.mobileDrawerOpen}`
                : styles.chatHistorySection
            }
            style={
              overlayRail
                ? undefined
                : { width: sidebarWidth, minWidth: sidebarWidth }
            }
            data-testid="console-rail"
          >
            <div className={styles.chatHistorySectionInner}>
              <div className={`cordexa-rail-scroll ${styles.chatHistoryContent}`}>
                {chatHistoryPanel}
              </div>
              {dockedRail ? (
                <div
                  className={styles.resizeHandle}
                  onMouseDown={handleDragStart}
                  role="separator"
                  aria-orientation="vertical"
                  aria-valuenow={sidebarWidth}
                  aria-valuemin={SIDEBAR_MIN_WIDTH}
                  aria-valuemax={getMaxSidebarWidth()}
                  title="Drag to resize"
                />
              ) : null}
            </div>
          </div>
        ) : null}
        <main
          className={styles.main}
          role="main"
          style={{ marginLeft: dockedRail ? sidebarWidth : 0 }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
